// ── Module-access policy (single source of truth) ────────────────────────────
// The entitlement→feature-access binding lives here so every gate — the hook,
// the route wrapper, any future call site — agrees on ONE default-on policy.
//
// Truth layer: public.active_module_keys(org) (migration 20260902_module_access)
// projects business_module_entitlements (Stripe-verified, admin-only) into a
// flat list of module keys that are ON for an org. The hook reads that RPC.
//
// This file only holds the *fail-safe* policy: what a gate should assume when
// the RPC has not answered yet (loading) or errored. The rule is deliberately
// asymmetric and honest:
//
//   * Flagship org 'parcelink' owns the whole platform today. A check hiccup
//     must NEVER hide a working feature from Parcelink, so the fail-safe is ON.
//     (The RPC agrees: for parcelink, a module with no explicit row defaults on.)
//   * Any second tenant is fail-safe OFF — we never grant a paid module from the
//     client on a failed/absent check. Gating only ever bites a second tenant.
//
// So for Parcelink this whole binding is a no-op (everything on, even on error);
// its value is future multi-tenant.

export const FLAGSHIP_ORG = 'parcelink';

/**
 * Fail-safe default when the entitlement projection is unavailable
 * (still loading, or the RPC errored). Never hides a live flagship feature;
 * never grants a paid module to a second tenant on a failed check.
 * @param {string} _moduleKey  module key (unused today; kept for future per-module policy)
 * @param {string} org         org key
 * @returns {boolean}
 */
export function isDefaultEnabled(_moduleKey, org = FLAGSHIP_ORG) {
  return org === FLAGSHIP_ORG;
}

/**
 * Resolve whether a module is enabled, given a (possibly incomplete) RPC result.
 * Pure — unit-testable without React or Supabase.
 *
 * When the projection has loaded cleanly we trust it verbatim (the RPC already
 * bakes in the flagship default-on). Until then, or on error, we fall back to
 * the fail-safe policy above.
 *
 * @param {object}   args
 * @param {string[]|null} args.keys      module keys from active_module_keys, or null
 * @param {string}   args.moduleKey      module to check, e.g. 'my-day'
 * @param {string}   args.org            org key
 * @param {boolean}  args.loaded         has the RPC answered?
 * @param {boolean}  args.error          did the RPC error?
 * @returns {boolean}
 */
export function resolveModuleEnabled({ keys, moduleKey, org = FLAGSHIP_ORG, loaded, error }) {
  if (!loaded || error || !Array.isArray(keys)) {
    return isDefaultEnabled(moduleKey, org);
  }
  return keys.includes(moduleKey);
}
